import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Spinner } from '@/components/atoms/Spinner';
import { NotFound } from '@/components/molecules/NotFound';
import { LICENSE_CODES, type LicenseCode } from '@/config/licenses';
import { useCage } from '@/contexts/cage';
import { useCageScopedSearch } from '@/contexts/cage-filter-context';
import { isBwBioscopeVersion, isColorBioscopeVersion } from '@/utils/bioscopeVersionHelpers';

import { usePenUrl } from '../../usePenUrl';

import { getHealthBwRedirectPath, getPenDashboardPath } from './_utils/welfareToHealthRedirects';
import HealthDashboardContainer from './HealthDashboard/HealthDashboardContainer';
import HealthDeformitiesDetailPage from './HealthDeformities/HealthDeformitiesDetailPage';
import HealthDetailPageContainer from './HealthDetailPage/HealthDetailPageContainer';
import HealthScaleSkinDetailPage from './HealthScaleSkin/HealthScaleSkinDetailPage';
import HealthWoundsBodyGraphsContainer from './HealthWounds/HealthWoundsBodyGraphs/HealthWoundsBodyGraphsContainer';
import HealthWoundsBodyInsightContainer from './HealthWounds/HealthWoundsBodyInsight/HealthWoundsBodyInsightContainer';
import HealthWoundsDetailPage from './HealthWounds/HealthWoundsDetailPage';

/** Old health paths and where they live now, both relative to `health/`. */
const LEGACY_REDIRECTS: ReadonlyArray<readonly [string, string]> = [
    ['body-wounds', 'body-wounds/laksvel'],
    ['wounds/body/laksvel', 'body-wounds/laksvel'],
    ['wounds/body/insight', 'body-wounds/insight'],
    ['wounds/body/graphs', 'body-wounds/graphs'],
    ['wounds/snout', 'snout-wounds'],
    ['wounds', 'body-wounds/laksvel'],
    ['scale-skin/scale-loss', 'scale-loss'],
    ['scale-skin/skin-bleeding', 'skin-bleeding'],
    ['scale-skin', 'scale-loss'],
    ['deformities', 'deformities/spine'],
];

/**
 * Wrapper for all Health module pages (its own routes under `health/*`).
 * Requires FISH_HEALTH.MIN and bioscope 2.0 / 3.0 (1.0 pens redirect to welfare).
 */
export default function HealthLayout() {
    const healthUrl = `${usePenUrl()}/health`;
    const location = useLocation();
    // Every redirect keeps the cage-scoped params, so the page it lands on keeps the fish group.
    const search = useCageScopedSearch();
    const { hasLicenseByCode, loading: cageLoading, bioscope } = useCage();
    const bioscopeVersion = bioscope?.version;
    const hasHealthLicense = hasLicenseByCode(LICENSE_CODES.FISH_HEALTH.MIN as LicenseCode);

    if (cageLoading) {
        return <Spinner size="lg" fullScreen />;
    }

    if (!hasHealthLicense) {
        return null;
    }

    if (isBwBioscopeVersion(bioscopeVersion)) {
        return <Navigate to={`${getHealthBwRedirectPath(location.pathname)}${search}`} replace />;
    }

    if (!isColorBioscopeVersion(bioscopeVersion)) {
        const redirectTo = getPenDashboardPath(location.pathname);
        return redirectTo ? <Navigate to={`${redirectTo}${search}`} replace /> : null;
    }

    return (
        <Routes>
            <Route index element={<HealthDashboardContainer />} />

            <Route
                path="body-wounds/laksvel"
                element={<HealthWoundsDetailPage pageId="bodyWounds" />}
            />
            <Route path="body-wounds/insight" element={<HealthWoundsBodyInsightContainer />} />
            <Route path="body-wounds/graphs" element={<HealthWoundsBodyGraphsContainer />} />
            <Route path="snout-wounds" element={<HealthWoundsDetailPage pageId="snoutWounds" />} />

            <Route path="maturation" element={<HealthDetailPageContainer pageId="maturation" />} />

            <Route path="scale-loss" element={<HealthScaleSkinDetailPage pageId="scaleLoss" />} />
            <Route
                path="skin-bleeding"
                element={<HealthScaleSkinDetailPage pageId="skinBleeding" />}
            />

            <Route path="fin-status" element={<HealthDetailPageContainer pageId="finStatus" />} />
            <Route path="emaciation" element={<HealthDetailPageContainer pageId="emaciation" />} />

            <Route
                path="deformities/spine"
                element={<HealthDeformitiesDetailPage pageId="spine" />}
            />
            <Route path="deformities/jaw" element={<HealthDeformitiesDetailPage pageId="jaw" />} />
            <Route
                path="deformities/gill-lid"
                element={<HealthDeformitiesDetailPage pageId="gillLid" />}
            />

            <Route path="cataract" element={<HealthDetailPageContainer pageId="cataract" />} />
            <Route path="eye-damage" element={<HealthDetailPageContainer pageId="eyeDamage" />} />

            {/* Older health URLs */}
            {LEGACY_REDIRECTS.map(([from, to]) => (
                <Route
                    key={from}
                    path={from}
                    element={<Navigate to={`${healthUrl}/${to}${search}`} replace />}
                />
            ))}
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}
